var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b, _c, _d, _e, _f;
import { action, observable, ObservableMap, makeObservable } from 'mobx';
import { compareImages } from '@bytebrand/fe-framework/images';
import config from '../../config/app.config';
import _flatten from 'lodash/flatten';
import ImagesAPI from '../services/ImagesAPI';
import { ImagePlaceholders, AdminImagePlaceholders, exteriorImageCodes, exteriorImageCodesOrder } from '../framework/constants/imageUpload';
import { ShowNotificationError } from '../framework/ErrorLogger';
import VehicleDetailsStore from './VehicleDetailsStore';
const BASE_IMAGE_URL = config.imagesAPI.imgUrl;
const IMAGE_CHECK_INTERVAL = 1000;
const DEFAULT_IMAGE_CODE = 'simple_temp';
class ImagesStore {
    constructor() {
        Object.defineProperty(this, "images", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "tempImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "processingImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "damageImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "exteriorImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "interiorImage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "imagePreviews", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable([])
        });
        Object.defineProperty(this, "imagePlaceholders", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ImagePlaceholders
        });
        Object.defineProperty(this, "adminImagePlaceholders", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: AdminImagePlaceholders
        });
        Object.defineProperty(this, "imagePreviewsCounter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        // true if browser supports webp image format
        Object.defineProperty(this, "webpFeature", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        // list of images that loading right now
        Object.defineProperty(this, "loading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        // force = true - is a temporary solution
        // includeAll includes all images, including the ones still in postprocessing
        Object.defineProperty(this, "getImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleID, force = true, includeAll = false, isAdmin = false, typeAlternative = '') => {
                const imagesObj = this.images.get(vehicleID);
                if (!force && imagesObj) {
                    return new Promise((resolve) => {
                        return resolve(imagesObj);
                    });
                }
                else { // eslint-disable-line
                    return this.processImages(vehicleID, includeAll, isAdmin, typeAlternative);
                }
            }
        });
        Object.defineProperty(this, "getTempImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleID) => {
                return this.loadImages(vehicleID, true);
            }
        });
        // @action getMainImage = (vehicleID: string, force: boolean) => {
        // const imagesObj = this.images.get(vehicleID);
        // TODO: implement getting main image
        // if ()
        // };
        Object.defineProperty(this, "checkWebpFeature", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                // If the browser doesn't has the method createImageBitmap, you can't display webp format
                if (!self.createImageBitmap)
                    return false;
                // Base64 representation of a white point image
                const webpData = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoCAAEAAQAcJaQAA3AA/v3AgAA=';
                // Retrieve the Image in Blob Format
                const blob = await fetch(webpData).then(r => r.blob());
                // If the createImageBitmap method succeeds, return true, otherwise false
                return createImageBitmap(blob).then(() => true, () => false);
            }
        });
        Object.defineProperty(this, "getSupportedImageFormat", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleID, imageIdentifier, size) => {
                const vehicleImageURL = `${BASE_IMAGE_URL}/${vehicleID}/${imageIdentifier}`;
                return this.webpFeature ? `${vehicleImageURL}/${size}-cached.webp` : `${vehicleImageURL}/${size}-cached.jpeg`;
            }
        });
        Object.defineProperty(this, "getImageTransparent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleID, imageIdentifier) => {
                const vehicleImageURL = `${BASE_IMAGE_URL}/${vehicleID}/${imageIdentifier}`;
                return vehicleImageURL;
            }
        });
        Object.defineProperty(this, "processImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleID, includeAll = false, isAdmin = false, typeAlternative) => {
                const loading = this.loading.indexOf(vehicleID) !== -1;
                if (loading) {
                    return this.waitImages(vehicleID);
                }
                return this.loadImages(vehicleID, isAdmin, includeAll, typeAlternative);
            }
        });
        Object.defineProperty(this, "waitImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleID) => new Promise((resolve) => {
                const loading = this.loading.indexOf(vehicleID) !== -1;
                const images = this.images.get(vehicleID);
                setTimeout(() => {
                    if ((images && images.length) && !loading) {
                        return resolve(images);
                    }
                    return resolve(this.waitImages(vehicleID));
                }, IMAGE_CHECK_INTERVAL); // eslint-disable-line
            })
        });
        // send temp: true if you need tempImages
        Object.defineProperty(this, "loadImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (vehicleID, temp = false, includeAll, typeAlternative) => {
                this.loading.push(vehicleID);
                if (!!typeAlternative) { // get alternative cars images (not imbo-service enhanced)
                    const currentTime = Math.round((new Date()).getTime() / 1000);
                    return VehicleDetailsStore.getAlternativeCarImages(vehicleID, true)
                        .then((images) => {
                        const imagesArr = images.map((imageUrl, id) => {
                            return {
                                id,
                                imageUrl,
                                imageUrlLarge: imageUrl,
                                imageUrlMedium: imageUrl,
                                imageUrlSmall: imageUrl,
                                downloadedAt: currentTime,
                                isSimpleTemp: true
                            };
                        });
                        this.images.set(vehicleID, imagesArr);
                        return this.images.get(vehicleID);
                    });
                }
                const retreiveImages = includeAll ? ImagesAPI.getRawCarImages : ImagesAPI.getCarImages;
                return retreiveImages(vehicleID).then((response) => {
                    const images = response.images;
                    const trImages = this.transformImages(images, vehicleID);
                    const { generalImages, tempImages, processingImages, damageImages, exteriorImages, interiorImage } = trImages;
                    this.images.set(vehicleID, generalImages);
                    this.tempImages.set(vehicleID, tempImages);
                    this.processingImages.set(vehicleID, processingImages);
                    this.damageImages.set(vehicleID, damageImages);
                    this.exteriorImages.set(vehicleID, exteriorImages);
                    this.interiorImage.set(vehicleID, interiorImage);
                    this.loading = this.loading.filter((id) => id !== vehicleID);
                    if (temp) {
                        return includeAll
                            ? [...this.images.get(vehicleID), ...this.tempImages.get(vehicleID)]
                            : this.tempImages.get(vehicleID);
                    }
                    return this.images.get(vehicleID);
                })
                    .catch((error) => {
                    ShowNotificationError();
                });
            }
        });
        Object.defineProperty(this, "transformImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (images, vehicleID) => {
                const milliseconds = 1000;
                const currentTime = Math.round((new Date()).getTime() / milliseconds);
                const imagesAll = {
                    generalImages: [],
                    tempImages: [],
                    processingImages: [],
                    damageImages: [],
                    exteriorImages: [],
                    interiorImage: null
                };
                const exteriorImages = exteriorImageCodesOrder.map(() => []);
                images.sort(compareImages);
                images.forEach((item) => {
                    const imageCode = item.metadata.image_code;
                    if (!imageCode)
                        return; // metadata must always contain image_code
                    const image = {
                        imageUrl: `${BASE_IMAGE_URL}/${vehicleID}/${item.imageIdentifier}`,
                        imageUrlLarge: this.getSupportedImageFormat(vehicleID, item.imageIdentifier, 'large'),
                        imageUrlMedium: this.getSupportedImageFormat(vehicleID, item.imageIdentifier, 'medium'),
                        imageUrlSmall: this.getSupportedImageFormat(vehicleID, item.imageIdentifier, 'small'),
                        imageId: item.imageIdentifier,
                        imageMeta: item.metadata,
                        downloadedAt: currentTime,
                        // Images with this flags we will show on vehicle details page
                        // whether it's my vehicle or vehicle from search.
                        isMain: imageCode === 'ext_f_right',
                        isInteriorMain: imageCode === 'int_f_ipanel1',
                        isPanel: imageCode === 'int_f_ipanel3',
                        isLegacy: item.metadata.legacy_image === '1',
                        isSimpleTemp: /^simple_\d{2}$/.test(imageCode),
                        // Images with this flags we will show on vehicle details page
                        // but under damages slider.
                        isDamage: imageCode.includes('dam_'),
                        // Images with this flag we only show on my vehicles detailed page.
                        isInProcessing: Number.parseInt(item.metadata.in_postprocessing, 10) === 1,
                        // Images with this flag we show only on image upload page
                        isTemp: imageCode === 'simple_temp',
                        // Images with this flags we will show on vehicle details page as 360 exterior
                        isExterior: exteriorImageCodes.has(imageCode),
                        // Images with this flags we will show on vehicle details page as 360 interior
                        isInterior: imageCode === 'int_middle_360'
                    };
                    if (image.isExterior && image.imageMeta.in_postprocessing !== 1) {
                        const index = exteriorImageCodesOrder.findIndex(codesOrder => codesOrder.has(imageCode));
                        exteriorImages[index].push(image);
                    }
                    if (image.isDamage) {
                        imagesAll.damageImages.push(image);
                        return false;
                    }
                    if (image.isInProcessing) {
                        imagesAll.processingImages.push(image);
                        return false;
                    }
                    // If it is just simple_xx image place it as usual
                    if (image.isSimpleTemp && !image.isMain) {
                        imagesAll.generalImages.push(image);
                        imagesAll.tempImages.push(image);
                        return false;
                    }
                    // If it's main image then place it on the first place
                    if (!image.isSimpleTemp && image.isMain) {
                        imagesAll.generalImages.unshift(image);
                        return false;
                    }
                    // If it is main image and simple image at the same time then add it
                    // in the end of temp images and in the beginning of the general images
                    if (image.isSimpleTemp && image.isMain) {
                        imagesAll.generalImages.unshift(image);
                        imagesAll.tempImages.push(image);
                        return false;
                    }
                    if (image.isTemp) {
                        imagesAll.tempImages.push(image);
                        return false;
                    }
                    if (image.isInterior) {
                        imagesAll.interiorImage = image;
                        return false;
                    }
                    imagesAll.generalImages.push(image);
                });
                exteriorImages.filter((images, index) => {
                    const codesOrder = exteriorImageCodesOrder[index];
                    if (images.length === codesOrder.size) {
                        images.sort((a, b) => codesOrder.get(a.imageMeta.image_code) - codesOrder.get(b.imageMeta.image_code));
                        return true;
                    }
                    return false;
                });
                imagesAll.exteriorImages = _flatten(exteriorImages);
                return imagesAll;
            }
        });
        Object.defineProperty(this, "getSortedImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (images) => {
                const imagePreviews = [];
                const imagePlaceholders = this.imagePlaceholders;
                const adminImagePlaceholders = this.adminImagePlaceholders;
                images.forEach((image) => {
                    if (!image)
                        return;
                    const { imageMeta: { image_code } } = image;
                    if (image_code) {
                        if (image_code === DEFAULT_IMAGE_CODE) {
                            imagePreviews.push(image);
                        }
                        if (image_code && image_code.indexOf('temp') === -1 && image_code.indexOf('simple') !== -1) {
                            let index = parseInt(image_code.split('_')[1], 10);
                            if (index) {
                                index = index - 1;
                                imagePlaceholders[index] = Object.assign({}, imagePlaceholders[index], { src: image.imageUrl, imageId: image.imageId });
                            }
                        }
                        else {
                            const imageIndex = adminImagePlaceholders.findIndex((imagePlaceholder) => imagePlaceholder.imageCode === image_code);
                            adminImagePlaceholders[imageIndex] = Object.assign({}, adminImagePlaceholders[imageIndex], { src: image.imageUrl, imageId: image.imageId });
                        }
                    }
                });
                this.imagePreviews = imagePreviews;
                this.imagePlaceholders = imagePlaceholders;
                this.adminImagePlaceholders = adminImagePlaceholders;
            }
        });
        Object.defineProperty(this, "updateImageUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (index, imageId, src) => {
                const state = this.imagePlaceholders;
                state[index] = Object.assign({}, state[index], { src, imageId });
                this.imagePlaceholders = state;
            }
        });
        Object.defineProperty(this, "updateImageCode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId, imageId, imageCode) => {
                // Dropping onto an occupied slot used to be a one-way overwrite: the
                // previous occupant kept its old code and the dragged image's old slot
                // was left with no image at all — repeated drags orphan slots permanently
                // (see ADV-144 image-reorder investigation). Swap the occupant into the
                // dragged image's old slot first so every slot keeps exactly one image.
                const occupant = this.imagePlaceholders.find((p) => p.imageCode === imageCode && p.imageId && p.imageId !== imageId);
                const source = this.imagePlaceholders.find((p) => p.imageId === imageId);
                const swap = occupant && source && source.imageCode !== DEFAULT_IMAGE_CODE
                    ? ImagesAPI.updateImageCode(carId, occupant.imageId, source.imageCode)
                    : Promise.resolve();
                swap.then(() => ImagesAPI.updateImageCode(carId, imageId, imageCode)).then(() => {
                    this.getImages(carId, true, true, true).then((images) => this.getSortedImages(images));
                    const newCounterValue = imageCode === DEFAULT_IMAGE_CODE ? this.imagePreviewsCounter + 1 : this.imagePreviewsCounter - 1;
                    this.updateImagePreviewsCounter(newCounterValue);
                })
                    .catch((error) => {
                    ShowNotificationError();
                });
            }
        });
        Object.defineProperty(this, "updateImageCodeToDefault", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId, imageId) => {
                this.updateImageCode(carId, imageId, DEFAULT_IMAGE_CODE);
                this.imagePlaceholders = this.imagePlaceholders.map((item) => {
                    if (item.imageId === imageId) {
                        item.imageId = '';
                        item.src = '';
                    }
                    return item;
                });
                this.adminImagePlaceholders = this.adminImagePlaceholders.map((item) => {
                    if (item.imageId === imageId) {
                        item.imageId = '';
                        item.src = '';
                    }
                    return item;
                });
            }
        });
        Object.defineProperty(this, "deleteImage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (carId, imageId) => {
                return ImagesAPI.deleteImage(carId, imageId).then(() => {
                    this.getImages(carId, true, true, true).then((images) => this.getSortedImages(images));
                    this.updateImagePreviewsCounter(this.imagePreviewsCounter - 1);
                })
                    .catch((error) => {
                    ShowNotificationError();
                });
            }
        });
        Object.defineProperty(this, "updateImagePreviewsCounter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.imagePreviewsCounter = value;
            }
        });
        Object.defineProperty(this, "clearProcessingImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id) => {
                if (id) {
                    this.processingImages.delete(id);
                }
                else {
                    this.processingImages.clear();
                }
            }
        });
        Object.defineProperty(this, "clearImagesStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.tempImages.clear();
                this.images.clear();
                this.imagePreviews.clear();
                this.imagePlaceholders = ImagePlaceholders;
                this.adminImagePlaceholders = AdminImagePlaceholders;
            }
        });
        makeObservable(this);
        this.checkWebpFeature().then((webpSupported) => {
            this.webpFeature = webpSupported;
        });
    }
}
__decorate([
    observable,
    __metadata("design:type", typeof (_a = typeof ObservableMap !== "undefined" && ObservableMap) === "function" ? _a : Object)
], ImagesStore.prototype, "images", void 0);
__decorate([
    observable,
    __metadata("design:type", typeof (_b = typeof ObservableMap !== "undefined" && ObservableMap) === "function" ? _b : Object)
], ImagesStore.prototype, "tempImages", void 0);
__decorate([
    observable,
    __metadata("design:type", typeof (_c = typeof ObservableMap !== "undefined" && ObservableMap) === "function" ? _c : Object)
], ImagesStore.prototype, "processingImages", void 0);
__decorate([
    observable,
    __metadata("design:type", typeof (_d = typeof ObservableMap !== "undefined" && ObservableMap) === "function" ? _d : Object)
], ImagesStore.prototype, "damageImages", void 0);
__decorate([
    observable,
    __metadata("design:type", typeof (_e = typeof ObservableMap !== "undefined" && ObservableMap) === "function" ? _e : Object)
], ImagesStore.prototype, "exteriorImages", void 0);
__decorate([
    observable,
    __metadata("design:type", typeof (_f = typeof ObservableMap !== "undefined" && ObservableMap) === "function" ? _f : Object)
], ImagesStore.prototype, "interiorImage", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ImagesStore.prototype, "imagePreviews", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ImagesStore.prototype, "imagePlaceholders", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ImagesStore.prototype, "adminImagePlaceholders", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ImagesStore.prototype, "imagePreviewsCounter", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagesStore.prototype, "getImages", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagesStore.prototype, "getTempImages", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagesStore.prototype, "getSortedImages", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagesStore.prototype, "updateImageUrl", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagesStore.prototype, "updateImageCode", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagesStore.prototype, "updateImageCodeToDefault", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagesStore.prototype, "deleteImage", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagesStore.prototype, "updateImagePreviewsCounter", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagesStore.prototype, "clearProcessingImages", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagesStore.prototype, "clearImagesStore", void 0);
export default new ImagesStore();
